import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useMemo } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { useIndicatorValues } from '~pages/LiveMap/Timeline/components/hooks/useIndicatorValues';
import { useTimeNormalization } from '~pages/LiveMap/Timeline/components/hooks/useTimeNormalization';
import TimeIndicator from '~pages/LiveMap/Timeline/components/TimeIndicator';

import {
  TimelineTickLabels,
  TimelineTickLines,
  TimelineTimeDisplay,
} from './timelineTicks';

export interface JobTimelineEntry {
  jobUuid: string;
  jobId: string;
  color: string;
}

const ROW_HEIGHT = 16;
const ROW_GAP = 4;
const NO_SIGNAL_THRESHOLD_MS = 15 * 60 * 1000;
const LABEL_WIDTH = 80;

interface TimeRange {
  start: string;
  end: string;
}

function computeJobSegments(
  pings: LocationHistoryMinimal[],
  jobs: JobTimelineEntry[],
): Map<string, TimeRange[]> {
  const grouped = new Map<string, LocationHistoryMinimal[]>();

  for (const p of pings) {
    if (!p.jobId) continue;
    if (!grouped.has(p.jobId)) grouped.set(p.jobId, []);
    grouped.get(p.jobId)!.push(p);
  }

  const result = new Map<string, TimeRange[]>();

  for (const job of jobs) {
    const jobPings = grouped.get(job.jobUuid);
    if (!jobPings || jobPings.length === 0) {
      result.set(job.jobUuid, []);
      continue;
    }

    const segments: TimeRange[] = [];
    let segStart = jobPings[0].occurredAt;
    let segEnd = jobPings[0].occurredAt;

    for (let i = 1; i < jobPings.length; i++) {
      const current = jobPings[i].occurredAt;
      if (current.valueOf() - segEnd.valueOf() > NO_SIGNAL_THRESHOLD_MS) {
        segments.push({
          start: segStart.toISOString(),
          end: segEnd.toISOString(),
        });
        segStart = current;
      }
      segEnd = current;
    }

    segments.push({
      start: segStart.toISOString(),
      end: segEnd.toISOString(),
    });

    result.set(job.jobUuid, segments);
  }

  return result;
}

interface Props {
  pings: LocationHistoryMinimal[];
  jobs: JobTimelineEntry[];
}

export const DistanceJobsTimeline = ({ pings, jobs }: Props) => {
  const theme = useTheme();
  const startMs = pings[0].occurredAt.valueOf();
  const endMs = pings[pings.length - 1].occurredAt.valueOf();
  const firstPingTime = pings[0].occurredAt.toISOString();
  const lastPingTime = pings[pings.length - 1].occurredAt.toISOString();

  const normalize = useTimeNormalization(firstPingTime, lastPingTime);
  const jobSegments = useMemo(() => computeJobSegments(pings, jobs), [pings, jobs]);

  const { graphRef, indicatorValues } = useIndicatorValues(
    firstPingTime,
    lastPingTime,
    [],
  );

  const graphHeight = jobs.length * ROW_HEIGHT + (jobs.length + 1) * ROW_GAP;

  return (
    <>
      <TimelineTimeDisplay
        indicatorTime={indicatorValues.indicatorTime}
        label={t('live_map.timeline.time')}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `${LABEL_WIDTH}px 1fr`,
          columnGap: 1,
        }}
      >
        {jobs.map((job, rowIndex) => (
          <Typography
            key={`label-${job.jobUuid}`}
            variant="body2"
            noWrap
            sx={{
              gridColumn: 1,
              gridRow: rowIndex + 1,
              lineHeight: `${ROW_HEIGHT}px`,
              mt: `${ROW_GAP}px`,
              color: job.color,
              fontFamily: 'monospace',
              fontSize: '0.6875rem',
            }}
          >
            {job.jobId}
          </Typography>
        ))}

        <Box sx={{ gridColumn: 2, gridRow: `1 / ${jobs.length + 2}` }}>
          <Box
            ref={graphRef}
            sx={{
              position: 'relative',
              height: graphHeight,
              border: `1px solid ${theme.brandV2.colors.treadGray6}`,
              borderRadius: theme.brandV2.borderRadius,
              backgroundColor: theme.palette.background.paper,
              overflow: 'hidden',
            }}
          >
            <TimeIndicator
              visible={indicatorValues.indicatorVisible}
              position={indicatorValues.indicatorPosition}
              time={indicatorValues.indicatorTime}
            />

            <TimelineTickLines startMs={startMs} endMs={endMs} normalize={normalize} />

            {jobs.map((job, rowIndex) => {
              const segments = jobSegments.get(job.jobUuid) ?? [];
              const top = ROW_GAP + rowIndex * (ROW_HEIGHT + ROW_GAP);

              return segments.map((seg, segIndex) => (
                <Box
                  key={`${job.jobUuid}-${segIndex}`}
                  sx={{
                    position: 'absolute',
                    left: normalize(seg.start),
                    right: normalize(seg.end, true),
                    height: ROW_HEIGHT,
                    top: top,
                    backgroundColor: job.color,
                    borderRadius: '2px',
                    minWidth: 2,
                  }}
                />
              ));
            })}
          </Box>

          <TimelineTickLabels startMs={startMs} endMs={endMs} normalize={normalize} />
        </Box>
      </Box>
    </>
  );
};
